import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useNavigate } from 'react-router-dom';

interface TemplateDeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  templateId: string;
  templateName: string;
}

export const TemplateDeleteConfirmDialog: React.FC<TemplateDeleteConfirmDialogProps> = ({
  open,
  onClose,
  templateId,
  templateName,
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await authenticatedFetch(
        `/api/deliverables/templates/${templateId}/delete`,
        {
          method: 'DELETE',
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Delete failed');
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate templates query to refresh list
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template', templateId] });

      // Navigate to templates list
      navigate('/admin/deliverables/templates');
      onClose();
    },
  });

  const handleClose = () => {
    if (!deleteMutation.isPending) {
      onClose();
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <WarningIcon color="warning" sx={{ fontSize: 40, mr: 1 }} />
          <Typography variant="h6">Delete Template</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Are you sure you want to delete this template?
        </Typography>

        <Alert severity="info" sx={{ my: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Template: {templateName}
          </Typography>
        </Alert>

        <Typography variant="body2" paragraph>
          This action will:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
          <Typography component="li" variant="body2" gutterBottom>
            Deactivate the template
          </Typography>
          <Typography component="li" variant="body2" gutterBottom>
            Remove it from your template list
          </Typography>
          <Typography component="li" variant="body2" gutterBottom>
            Existing deliverables will retain their generated documents
          </Typography>
        </Box>

        <Typography variant="body2" fontWeight={600} color="error">
          This action cannot be undone.
        </Typography>

        {/* Error Display */}
        {deleteMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteMutation.error instanceof Error
              ? deleteMutation.error.message
              : 'An error occurred while deleting the template'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={deleteMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
          startIcon={deleteMutation.isPending ? <CircularProgress size={20} /> : null}
        >
          {deleteMutation.isPending ? 'Deleting...' : 'Delete Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
